import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, MapPin, Building, Calendar, Shield, Clock, Filter, ArrowRight, Loader2, ChevronDown, X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PublicLayout, { PageHero } from '../components/public/PublicLayout';
import { EASE, Reveal } from '../components/public/motion';
import { publicSupabase as supabase } from '../lib/publicSupabaseClient';
import { formatDate } from '../utils/dateFormatter';

const isEOIDate = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr).getFullYear() > 2050;
};

const CLEARANCE_COLORS = {
  TSPV: 'bg-purple-100 text-purple-700 border-purple-200',
  NV2: 'bg-blue-100 text-blue-700 border-blue-200',
  NV1: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  Baseline: 'bg-slate-100 text-slate-700 border-slate-200',
  None: 'bg-slate-50 text-slate-500 border-slate-100',
};

const FilterGroup = ({ label, options, selected, onToggle }) => {
  if (!options.length) return null;
  return (
    <div>
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-all duration-200 ${
              selected.includes(opt)
                ? 'border-red-500 bg-red-500 text-white'
                : 'border-gray-200 text-gray-600 hover:border-red-400 hover:text-red-500'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

const JobCard = ({ job }) => (
  <motion.article
    layout
    initial={{ opacity: 0, scale: 0.97, y: 16 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97, y: -10 }}
    transition={{ duration: 0.35, ease: EASE }}
    whileHover={{ y: -5 }}
    className="group flex flex-col rounded-3xl border border-gray-200 bg-white p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-gray-900/5"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {job.work_arrangement && (
          <p className="text-xs font-semibold uppercase tracking-widest text-red-500">{job.work_arrangement}</p>
        )}
        <h3 className="mt-1 font-display text-xl font-bold leading-tight text-gray-900">{job.title}</h3>
      </div>
      {job.clearance_required && job.clearance_required !== 'None' && (
        <span className={`mt-1 inline-flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${CLEARANCE_COLORS[job.clearance_required] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
          <Shield className="h-3 w-3" />
          {job.clearance_required}
        </span>
      )}
    </div>

    <div className="mt-3 flex items-center gap-1.5 text-sm text-gray-500">
      <Building className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      <span className="font-medium">{job.client?.name || 'Confidential'}</span>
      {job.ref_code && <span className="ml-1 text-gray-400">· {job.ref_code}</span>}
    </div>

    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500">
      {job.locations?.length > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-gray-400" />
          {Array.isArray(job.locations) ? job.locations.join(', ') : job.locations}
        </span>
      )}
      {job.client_close_datetime && (
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-gray-400" />
          {isEOIDate(job.client_close_datetime) ? 'Rolling EOI' : `Closes ${formatDate(job.client_close_datetime, { style: 'short' })}`}
        </span>
      )}
      {job.initial_term_months && (
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-gray-400" />
          {job.initial_term_months}mo{job.extension_options ? ` + ${job.extension_options}` : ''}
        </span>
      )}
    </div>

    <div className="mt-auto flex gap-3 pt-6">
      <Link
        to={`/job/${job.id}`}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/25"
      >
        View Details <ArrowRight className="h-4 w-4" />
      </Link>
      <Link
        to={`/job/${job.id}/apply`}
        className="inline-flex flex-1 items-center justify-center rounded-full border-2 border-gray-900 px-5 py-2.5 text-sm font-semibold text-gray-900 transition-all duration-300 hover:bg-gray-900 hover:text-white"
      >
        Apply now
      </Link>
    </div>
  </motion.article>
);

const JobBoard = () => {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedClearances, setSelectedClearances] = useState([]);
  const [selectedWorkArrangements, setSelectedWorkArrangements] = useState([]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('rfqs')
          .select('*, client:clients(name)')
          .eq('display_status', 'visible')
          .gte('client_close_datetime', new Date().toISOString())
          .order('created_at', { ascending: false });
        if (error) throw error;
        setRfqs(data || []);
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filterOptions = useMemo(() => {
    const locations = new Set();
    const clearances = new Set();
    const workArrangements = new Set();
    rfqs.forEach((rfq) => {
      if (Array.isArray(rfq.locations)) rfq.locations.forEach((l) => locations.add(l));
      if (rfq.clearance_required) clearances.add(rfq.clearance_required);
      if (rfq.work_arrangement) workArrangements.add(rfq.work_arrangement);
    });
    return {
      locations: Array.from(locations).sort(),
      clearances: Array.from(clearances).sort(),
      workArrangements: Array.from(workArrangements).sort(),
    };
  }, [rfqs]);

  const filteredJobs = useMemo(() => {
    let list = rfqs;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          r.title?.toLowerCase().includes(q) ||
          r.ref_code?.toLowerCase().includes(q) ||
          r.client?.name?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q)
      );
    }
    if (selectedLocations.length) list = list.filter((r) => r.locations?.some((l) => selectedLocations.includes(l)));
    if (selectedClearances.length) list = list.filter((r) => selectedClearances.includes(r.clearance_required));
    if (selectedWorkArrangements.length) list = list.filter((r) => selectedWorkArrangements.includes(r.work_arrangement));
    return list;
  }, [rfqs, searchTerm, selectedLocations, selectedClearances, selectedWorkArrangements]);

  const toggle = (value, setter, current) =>
    setter(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);

  const clearAll = () => {
    setSelectedLocations([]);
    setSelectedClearances([]);
    setSelectedWorkArrangements([]);
    setSearchTerm('');
  };

  const activeCount = selectedLocations.length + selectedClearances.length + selectedWorkArrangements.length;
  const hasActive = activeCount > 0 || searchTerm.trim() !== '';

  return (
    <PublicLayout>
      <PageHero
        eyebrow="ICT opportunities"
        title="Find your next government role"
        subtitle="Cleared ICT positions from Australian public sector clients: contract and permanent across all disciplines."
      />

      <section className="bg-gray-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">

          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin text-red-500" />
              <p className="text-sm">Loading job listings…</p>
            </div>
          ) : (
            <>
              {/* Filter bar */}
              <Reveal>
                <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-lg shadow-gray-900/5 md:p-6">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by title, client, ref code…"
                        className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-sm transition-all duration-200 focus:border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                          showFilters || activeCount > 0
                            ? 'border-red-500 bg-red-500 text-white'
                            : 'border-gray-200 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        <Filter className="h-4 w-4" />
                        Filters
                        {activeCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-red-500">
                            {activeCount}
                          </span>
                        )}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
                      </button>
                      {hasActive && (
                        <button
                          onClick={clearAll}
                          className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-red-500"
                        >
                          <X className="h-4 w-4" /> Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <AnimatePresence>
                    {showFilters && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="mt-5 grid grid-cols-1 gap-6 border-t border-gray-100 pt-5 md:grid-cols-3">
                          <FilterGroup
                            label="Location"
                            options={filterOptions.locations}
                            selected={selectedLocations}
                            onToggle={(v) => toggle(v, setSelectedLocations, selectedLocations)}
                          />
                          <FilterGroup
                            label="Security Clearance"
                            options={filterOptions.clearances}
                            selected={selectedClearances}
                            onToggle={(v) => toggle(v, setSelectedClearances, selectedClearances)}
                          />
                          <FilterGroup
                            label="Work Arrangement"
                            options={filterOptions.workArrangements}
                            selected={selectedWorkArrangements}
                            onToggle={(v) => toggle(v, setSelectedWorkArrangements, selectedWorkArrangements)}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>

              <div className="mb-6 mt-8">
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{filteredJobs.length}</span>{' '}
                  {filteredJobs.length === 1 ? 'role' : 'roles'} available
                </p>
              </div>

              {filteredJobs.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-16 text-center">
                  <p className="font-display text-xl font-bold text-gray-900">No matching roles</p>
                  <p className="mt-2 text-gray-500">
                    {hasActive
                      ? 'Try adjusting your search or clearing some filters.'
                      : 'No listings available right now. Check back soon.'}
                  </p>
                  {hasActive && (
                    <button
                      onClick={clearAll}
                      className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-gray-900 px-6 py-2.5 text-sm font-semibold text-gray-900 transition-all hover:bg-gray-900 hover:text-white"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))}
                  </div>
                </AnimatePresence>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default JobBoard;
